import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { AccordianItem } from '.';

export interface AccordianContextType {
  items: AccordianItem[];
  openId: string | undefined;
  onClick: (newId: string) => void;
  milliseconds: number;
  disabled: boolean;
  accordianContentClassName: string;
}

export const AccordianContext = createContext<AccordianContextType | null>(
  null,
);

export const useAccordianContext = () =>
  useContext(AccordianContext) as AccordianContextType;

export interface AccordianProviderProps {
  children: ReactNode;
  items: AccordianItem[];
  accordianClassName?: string;
  milliseconds?: number;
}

const AccordianProvider: FunctionComponent<AccordianProviderProps> = function ({
  children,
  items,
  accordianClassName,
  milliseconds = 300,
}) {
  const [openId, setOpenId] = useState<string | undefined>();
  const [disabled, setDisabled] = useState<boolean>(false);

  const handleDisable = useCallback(() => {
    setDisabled(true);
    setTimeout(() => setDisabled(false), milliseconds);
  }, [milliseconds]);

  const onClick = useCallback(
    (newId: string) => {
      if (disabled) return;
      handleDisable();
      if (newId === openId) {
        setOpenId(undefined);
        return;
      }
      setOpenId(newId);
    },
    [handleDisable, openId, disabled],
  );

  const accordianContentClassName = useMemo(
    () =>
      [
        'flex flex-col w-full',
        accordianClassName && /^.*gap-y-[1-9][0-9]*.*$/.test(accordianClassName)
          ? ''
          : 'gap-y-4',
        accordianClassName,
      ]
        .filter(Boolean)
        .join(' '),
    [accordianClassName],
  );

  const values = useMemo(
    () => ({
      items,
      openId,
      onClick,
      milliseconds,
      disabled,
      accordianContentClassName,
    }),
    [items, openId, onClick, milliseconds, disabled, accordianContentClassName],
  );

  return (
    <AccordianContext.Provider value={values}>
      {children}
    </AccordianContext.Provider>
  );
};

export default AccordianProvider;
